import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { BackLink, recordGate } from '@/components/record';
import CopyButton from '@/components/admin/CopyButton';
import TemplateDialog, { KIND_USES, SAMPLE_VARS, unknownPlaceholders } from '@/components/admin/TemplateDialog';
import { TEMPLATE_KIND_STYLES, TEMPLATE_PLACEHOLDERS } from '@/shared/constants';
import { TEMPLATE_FIELDS } from '@/shared/fields';
import { committed, logActivity } from '@/shared/data';
import { renderTemplate } from '@/shared/messages';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/templates/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

export default function AdminTemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: template, loading, error, run } = useFetch(
    () => sdk.table('templates_c').select(TEMPLATE_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/templates" label="Back to Templates" />;
  const gate = recordGate({ back, loading, record: template, error, run, what: 'Template' });
  if (gate) return gate;

  const preview = renderTemplate(template, SAMPLE_VARS);
  const raw = `${template.subject_c ?? ''} ${template.body_c ?? ''}`;
  const used = TEMPLATE_PLACEHOLDERS.filter((t) => raw.includes(t));
  const unknown = unknownPlaceholders(raw);

  async function duplicate() {
    const res = await sdk.table('templates_c').create({
      Name: `${template.Name} (copy)`,
      kind_c: template.kind_c,
      subject_c: template.subject_c ?? '',
      body_c: template.body_c ?? '',
    });
    const saved = committed(res, 'Template duplicated');
    if (!saved) return false;
    logActivity('Email', 'Template duplicated', template.Name, template.kind_c);
    navigate(saved.Id ? `/admin/templates/${saved.Id}` : '/admin/templates');
    return true;
  }

  async function remove() {
    const res = await sdk.table('templates_c').remove(template.Id);
    if (!committed(res, 'Template deleted')) return false;
    logActivity('Email', 'Template deleted', template.Name, template.kind_c);
    navigate('/admin/templates');
    return true;
  }

  return (
    <div className="mx-auto max-w-5xl">
      {back}
      <PageHeader
        eyebrow={`Template · ${KIND_USES[template.kind_c] ?? template.kind_c}`}
        title={template.Name}
        actions={
          <>
            <StatusBadge value={template.kind_c} styles={TEMPLATE_KIND_STYLES} />
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            <ConfirmAction
              label="Duplicate"
              icon="CopyPlus"
              title="Duplicate this template?"
              description="A copy opens so you can write a variation without touching this one."
              busyLabel="Duplicating…"
              onConfirm={duplicate}
            />
            <ConfirmDelete
              title={`Delete ${template.Name}?`}
              description="Orders keep working. The order screen just stops offering this text."
              onConfirm={remove}
            />
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Panel
          title="Preview with a sample order"
          icon="Mail"
          bodyClassName="py-4"
          actions={<CopyButton text={`${preview.subject}\n\n${preview.body}`} label="Copy" />}
        >
          <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Subject</p>
          <p className="mt-1 font-medium">{preview.subject}</p>
          <p className="mt-4 text-xs uppercase tracking-[0.16em] text-muted-foreground">Body</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{preview.body}</p>
        </Panel>

        <div className="space-y-4">
          <Panel title="Details" icon="Info">
            <dl className="divide-y divide-border">
              <DetailRow label="Name">{template.Name}</DetailRow>
              <DetailRow label="Used for">{template.kind_c}</DetailRow>
              <DetailRow label="Added">{formatLocalDate(template.CreatedOn, DATE_FORMATS.SHORT)}</DetailRow>
            </dl>
          </Panel>
          <Panel title="Placeholders" icon="Braces" bodyClassName="py-3">
            {used.length ? (
              <div className="flex flex-wrap gap-1.5">
                {used.map((t) => (
                  <span key={t} className="rounded-sm border border-border bg-muted px-2 py-1 font-mono text-xs">
                    {t}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">None. Every customer gets the same words.</p>
            )}
            {unknown.length > 0 && (
              <p className="mt-3 text-xs text-destructive">
                {unknown.join(', ')} will be sent as written. Edit the template to fix {unknown.length === 1 ? 'it' : 'them'}.
              </p>
            )}
          </Panel>
        </div>
      </div>

      <Panel title="As written" icon="FileText" className="mt-4" bodyClassName="py-4">
        <dl className="divide-y divide-border">
          <DetailRow label="Email subject">
            <span className="font-mono text-sm">{template.subject_c}</span>
          </DetailRow>
          <DetailRow label="Email body">
            <span className="whitespace-pre-wrap font-mono text-sm">{template.body_c}</span>
          </DetailRow>
        </dl>
      </Panel>

      <TemplateDialog open={editOpen} onOpenChange={setEditOpen} template={template} onSaved={run} />
    </div>
  );
}
